import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form, FormItem, FormMessage, FormField, FormControl, FormLabel } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import z from "zod";
import { ParkingPaymentModeType, PaymentZoneArea, SavePaymentZoneAreaPayload } from "../payment-zone-area.type";
import { FeatureCollection } from "geojson";
import { Dispatch, SetStateAction } from "react";

type PaymentZoneAreaPromptModalProps = {
  appMode: ParkingPaymentModeType;
  paymentZoneAreaDetail: PaymentZoneArea | undefined;
  paymentZoneAreaEditionData: FeatureCollection;
  setPaymentAreaZoneEditionData: Dispatch<SetStateAction<FeatureCollection>>;
  setPaymentZoneAreaModal: VoidFunction;
  savePaymentZoneAreaProperties: (payload: SavePaymentZoneAreaPayload) => Promise<void>;
};

const formSchema = z.object({
  name: z.string().trim().min(1, { message: "Name is required" }),
  cityId: z.string(),
  publishedDate: z.date().optional(),
});

type PaymentZoneAreaProperties = z.infer<typeof formSchema>;

export const PaymentZoneAreaPromptModal = ({
  appMode,
  paymentZoneAreaDetail,
  paymentZoneAreaEditionData,
  setPaymentZoneAreaModal,
  savePaymentZoneAreaProperties,
  setPaymentAreaZoneEditionData,
}: PaymentZoneAreaPromptModalProps) => {
  const form = useForm<PaymentZoneAreaProperties>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: paymentZoneAreaDetail?.name ?? "",
      cityId: paymentZoneAreaDetail?.cityId ?? "",
    },
    mode: "all",
  });

  const submit = async (formValues: PaymentZoneAreaProperties) => {
    await savePaymentZoneAreaProperties({
      properties: formValues,
    });
  };

  const handleClose = () => {
    if (appMode === ParkingPaymentModeType.AddPaymentZoneArea) {
      setPaymentAreaZoneEditionData({
        type: "FeatureCollection",
        features: paymentZoneAreaEditionData.features.slice(0, -1),
      });
    }
    setPaymentZoneAreaModal();
    form.reset();
  };

  return (
    <Dialog open>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{`${appMode === ParkingPaymentModeType.AddPaymentZoneArea ? "Add a new" : "Edit a"} Payment zone area`}</DialogTitle>
          <DialogDescription>Payment zone area information</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(submit)} className="space-y-6">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Name<span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter className="sm:justify-between">
              <Button type="button" variant="secondary" onClick={handleClose}>
                Close
              </Button>
              <Button type="submit">Save</Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
